import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '../lib/apiClient'

export type Theme = 'auto' | 'light' | 'dark'

export interface SettingsUserPreferences {
  upn: string
  email: string
  theme: Theme
  notifyOnNewSnapshot: boolean
  /** Spec 101 — recevoir le digest mensuel (opt-out grossier ; défaut abonné). */
  notifyOnDigest: boolean
  /** Spec 054 §11.4 — switch global Licences « Comparer avec le tarif en vigueur ». Persisté SQL. */
  comparePricesEnabled: boolean
}

export type UserPreferencesPatch = Partial<Omit<SettingsUserPreferences, 'upn' | 'email'>> & { email?: string | null }

const USE_MOCK = import.meta.env['VITE_USE_MOCK'] === 'true'

const mockPreferences: SettingsUserPreferences = {
  upn: 'demo@contoso.fr',
  email: 'demo@contoso.fr',
  theme: 'auto',
  notifyOnNewSnapshot: true,
  notifyOnDigest: true,
  comparePricesEnabled: false,
}

interface UseUserPreferencesState {
  preferences: SettingsUserPreferences | null
  loading: boolean
  saving: boolean
  error: string | null
  updatePreferences: (patch: UserPreferencesPatch) => Promise<void>
}

export function useUserPreferences(): UseUserPreferencesState {
  const [preferences, setPreferences] = useState<SettingsUserPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (USE_MOCK) {
      setPreferences(mockPreferences)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    apiFetch<SettingsUserPreferences>('/api/settings/preferences')
      .then(res => { if (!cancelled) { setPreferences(res); setLoading(false) } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erreur de chargement')
          setLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [])

  const updatePreferences = useCallback(async (patch: UserPreferencesPatch): Promise<void> => {
    if (USE_MOCK) {
      setPreferences(prev => {
        if (!prev) return prev
        const email = patch.email !== undefined ? (patch.email ?? prev.upn) : prev.email
        const { email: _e, ...rest } = patch
        void _e
        return { ...prev, ...rest, email }
      })
      return
    }

    setSaving(true)
    try {
      const result = await apiFetch<SettingsUserPreferences>('/api/settings/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      setPreferences(result)
    } finally {
      setSaving(false)
    }
  }, [])

  return { preferences, loading, saving, error, updatePreferences }
}
